/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { noop, debounce } from "lodash";
import IconClose from "@mui/icons-material/Close";
import IconMagnify from "@mui/icons-material/Search";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface ISearchInput {
  className?: string;
  value: string;
  placeholder?: string;
  onChange: (e: string) => void;
  onReset?: () => void;
  onFocus?: () => void;
  showResetButton?: boolean;
  debounceTime?: number;
  normalizeSearchQuery?: (key: string) => string;
  minCharacters?: number;
  insufficientCharactersMessage?: string;
}

export const SearchInput = (props: ISearchInput) => {
  const {
    className,
    value,
    placeholder,
    onChange = noop,
    onReset = noop,
    onFocus = noop,
    showResetButton,
    debounceTime = 800,
    normalizeSearchQuery,
    minCharacters,
    insufficientCharactersMessage,
  } = props;

  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Read through a ref so the debounced function stays stable: call sites pass an
  // inline onChange, and rebuilding the debounce on every parent render would run
  // the cleanup below and cancel the keystroke still waiting to be searched.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const debouncedOnChange = useMemo(
    () =>
      debounce((newValue: string) => {
        onChangeRef.current(newValue);
      }, debounceTime),
    [debounceTime]
  );

  // Cleanup debounce
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleReset = () => {
    debouncedOnChange.cancel();
    setInputValue("");
    onReset();
  };

  const shouldShowInsufficientCharactersMessage =
    minCharacters &&
    insufficientCharactersMessage &&
    inputValue.length > 0 &&
    (normalizeSearchQuery ? normalizeSearchQuery(inputValue) : inputValue)
      .length < minCharacters;

  return (
    <TextField
      className={`${className} marapp-qa-searchbox`}
      variant="outlined"
      fullWidth={true}
      placeholder={placeholder}
      value={inputValue}
      onChange={handleChange}
      onFocus={onFocus}
      InputProps={{
        className: "marapp-qa-searchboxinput",
        startAdornment: (
          <InputAdornment position="start">
            <IconMagnify fontSize="large" />
          </InputAdornment>
        ),
        endAdornment: showResetButton ? (
          <InputAdornment position="start">
            <IconButton
              onClick={handleReset}
              className="marapp-qa-searchboxclear"
              size="small"
            >
              <IconClose fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      {...(shouldShowInsufficientCharactersMessage
        ? { helperText: insufficientCharactersMessage }
        : {})}
    />
  );
};
